import { useMutation, useQuery } from "@tanstack/react-query";
import { useFirebaseAuthUser } from "@utils/firebaseAuth/query/auth/useFirebaseAuthUser";
import { useEffect, useMemo } from "react";
import { AuthStatus } from "types/auth";
import * as Intercom from "@utils/intercom";
import { captureException } from "@utils/captureException";
import * as rudderstack from "@utils/rudderstack";
import { useApi, useAuthSession } from "../../runtime/provider";
export { useAuthStore } from "../../runtime/provider";
import { AUTH_QUERY_KEY, USER_EXISTS_IN_DB_QUERY_KEY } from "@utils/queryKeys";

const useAuth = ({
  onError,
  suppressEmailVerificationResend = false,
}: {
  onError?: (error: unknown) => void;
  suppressEmailVerificationResend?: boolean;
} = {}) => {
  const auth = useAuthSession();
  const api = useApi();
  const firebaseAuthQuery = useFirebaseAuthUser([AUTH_QUERY_KEY], auth, {
    queryKey: [AUTH_QUERY_KEY],
    gcTime: Infinity,
    staleTime: Infinity,
  });

  const { mutate: logUserOut } = useMutation({ mutationFn: auth.signOut });

  const authEndpoint = useMutation({
    mutationFn: (input: Parameters<typeof api.user.auth.mutate>[0]) =>
      api.user.auth.mutate(input),
    onSuccess: (data) => {
      console.log("::useAuth - authEndpoint.onSuccess", data);
      const isSignup = data.isSignup;

      Intercom.loginUserWithUserAttributes({
        userId: firebaseAuthQuery.data?.uid,
        email: firebaseAuthQuery.data?.email ?? "",
      }).catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "support",
          operation: "intercom-login-after-auth",
        });
      });
      rudderstack.track(isSignup ? "signup" : "login", {
        email: data.email ?? "",
      });
    },
  });

  const authQuery = useQuery({
    queryKey: [USER_EXISTS_IN_DB_QUERY_KEY],
    queryFn: async () => {
      const startedAt = globalThis.performance?.now?.() ?? Date.now();
      console.log("::AuthTiming:: auth-endpoint-start", {
        suppressEmailVerificationResend,
      });
      const authEndpointRes = await authEndpoint.mutateAsync({
        suppressEmailVerificationResend,
      });
      const endedAt = globalThis.performance?.now?.() ?? Date.now();
      console.log("::AuthTiming:: auth-endpoint-success", {
        elapsedMs: Math.round(endedAt - startedAt),
        isSignup: authEndpointRes.isSignup,
        onboardingStep: authEndpointRes.onboardingStep,
        suppressEmailVerificationResend,
      });
      return authEndpointRes;
    },
    staleTime: suppressEmailVerificationResend ? 0 : Infinity,
    retry: false,
    gcTime: Infinity,
    refetchOnMount: false,
    enabled: !!firebaseAuthQuery.data,
    networkMode: "always",
  });

  const refetch = authQuery.refetch;

  const status: AuthStatus = useMemo(() => {
    if (
      firebaseAuthQuery.status === "pending" ||
      (firebaseAuthQuery.data?.refreshToken && !auth.currentUser())
    ) {
      //we are waiting for firebase auth
      return "firebase-auth-pending";
    }

    if (firebaseAuthQuery.data?.uid) {
      if (!authQuery.data) {
        if (authQuery.isFetching) {
          //we are waiting for db user
          return "db-user-pending";
        }

        if (authQuery.error) {
          //we have an error
          logUserOut();
          onError?.(authQuery.error);

          return "db-user-error";
        }
      }

      if (authQuery.data?.status === "ok") {
        return "logged-in";
      }

      //we have firebase user
      return "firebase-user-logged-in";
    }

    //we have no firebase user and no db user
    return "no-user";
  }, [
    auth,
    firebaseAuthQuery.status,
    firebaseAuthQuery.data?.uid,
    authQuery.data,
    authQuery.isFetching,
    authQuery.error,
    onError,
    logUserOut,
    firebaseAuthQuery.data?.refreshToken,
  ]);

  useEffect(() => {
    auth.store.setState({ status });
  }, [auth, status]);

  const onboardingStep = authQuery.data?.onboardingStep;
  const isSignup = authQuery.data?.isSignup;
  const email = authQuery.data?.email;

  return {
    status,
    refetch,
    firebaseUser: firebaseAuthQuery.data,
    email,
    onboardingStep,
    isSignup,
  };
};

export { useAuth };
